'use client';

import Link from '@/components/compat/Link';
import { GsImage } from '@/components/help-center/latest-power-shared';
import { formatPowerDisplayName } from '@/lib/power-mention-data';
import type { FairtradePowerRow } from '@/lib/fairtrade-catalog';
import {
  getFairtradeRowCategoryDisplay,
  getFairtradeRowDisplayPrices,
  type FairtradeCollectionPriceStats,
} from '@/lib/fairtrade-catalog';
import {
  FairtradeAvailabilityBadge,
  FairtradeCategoryBadge,
  FairtradeDaysValue,
  FairtradeIdBadge,
  FairtradeNewBadge,
  FairtradeStoreValue,
  FairtradeXatsValue,
} from '@/components/help-center/fairtrade/fairtrade-shared';
import PowerFairtradeHistoryDialog from '@/components/help-center/powers/PowerFairtradeHistoryDialog';
import FairtradeCollectionActions from '@/components/help-center/fairtrade/FairtradeCollectionActions';
import { cn } from '@/lib/utils';

function historySubject(row: FairtradePowerRow) {
  return {
    slug: row.slug,
    name: formatPowerDisplayName(row.name),
    prices: {
      fairtrade_min: row.minXats,
      fairtrade_max: row.maxXats,
      fairtrade_min_days: row.minDays,
      fairtrade_max_days: row.maxDays,
    },
  };
}

export default function FairtradeMobileList({
  rows,
  isDark = false,
  activeFilter = '',
  latestPowerId = null,
  collectionPrices,
}: {
  rows: FairtradePowerRow[];
  isDark?: boolean;
  /** Filtro activo (categoria ou família de coleção). */
  activeFilter?: string;
  latestPowerId?: number | null;
  collectionPrices?: Map<string, FairtradeCollectionPriceStats>;
}) {
  return (
    <ul className="m-0 flex list-none flex-col gap-2.5 p-0 md:hidden">
      {rows.map((row) => {
        const categoryDisplay = getFairtradeRowCategoryDisplay(row, activeFilter);
        const isLatest = latestPowerId != null && row.id === latestPowerId;
        const prices = getFairtradeRowDisplayPrices(row, collectionPrices);
        return (
          <li key={row.id}>
            <div
              className={cn(
                'rounded-2xl border p-3.5 shadow-sm transition-colors',
                isLatest
                  ? isDark
                    ? 'border-sky-400/35 bg-gradient-to-br from-sky-500/15 via-[#01012e] to-[#01012e] text-sky-50 shadow-sky-950/40 ring-1 ring-sky-400/20'
                    : 'border-sky-300 bg-gradient-to-br from-sky-50 via-white to-white text-gray-900 ring-1 ring-sky-200/80'
                  : isDark
                    ? 'border-blue-900/60 bg-[#01012e] text-sky-50 shadow-black/25'
                    : 'border-gray-200/90 bg-white text-gray-900'
              )}
            >
              <Link
                href={`/powers/${encodeURIComponent(row.slug)}`}
                className={cn(
                  'block no-underline outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
                  isDark
                    ? 'text-sky-50 hover:text-sky-200'
                    : 'text-gray-900 hover:text-sky-800'
                )}
              >
                <div className="flex items-start gap-2.5">
                  <FairtradeIdBadge id={row.id} isDark={isDark} />
                  <GsImage
                    code={row.name}
                    className="flex h-10 w-10 shrink-0 items-center justify-center"
                    imgClassName="max-h-10 max-w-10 object-contain drop-shadow-sm"
                    sizePx={40}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <p
                        className={cn(
                          'truncate font-poppins text-sm font-semibold',
                          isDark ? '!text-sky-50' : '!text-gray-900'
                        )}
                      >
                        {formatPowerDisplayName(row.name)}
                      </p>
                      {isLatest ? <FairtradeNewBadge isDark={isDark} /> : null}
                    </div>
                    {row.title ? (
                      <p
                        className={cn(
                          'truncate font-inter text-xs',
                          isDark ? '!text-blue-200/75' : '!text-gray-500'
                        )}
                      >
                        {row.title}
                      </p>
                    ) : null}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <FairtradeCategoryBadge
                        type={row.type}
                        typeLabel={categoryDisplay.label}
                        category={categoryDisplay.categoryKey}
                      />
                      <FairtradeAvailabilityBadge limited={row.flags.limited} isDark={isDark} />
                    </div>
                  </div>
                </div>
              </Link>

              <div
                className={cn(
                  'mt-3 grid grid-cols-2 gap-2 border-t pt-3 sm:grid-cols-3',
                  isDark ? 'border-blue-900/50' : 'border-gray-100'
                )}
              >
                <div>
                  <p
                    className={cn(
                      'font-poppins text-[10px] font-semibold uppercase tracking-wide',
                      isDark ? '!text-sky-200/80' : '!text-gray-500'
                    )}
                  >
                    Xats
                  </p>
                  <div className="mt-0.5">
                    <FairtradeXatsValue
                      min={prices.minXats}
                      max={prices.maxXats}
                      className="text-sm font-bold"
                      isDark={isDark}
                    />
                  </div>
                </div>
                <div>
                  <p
                    className={cn(
                      'font-poppins text-[10px] font-semibold uppercase tracking-wide',
                      isDark ? '!text-sky-200/80' : '!text-gray-500'
                    )}
                  >
                    Days
                  </p>
                  <div className="mt-0.5">
                    <FairtradeDaysValue
                      min={prices.minDays}
                      max={prices.maxDays}
                      className="text-sm font-bold"
                      isDark={isDark}
                    />
                  </div>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <p
                    className={cn(
                      'font-poppins text-[10px] font-semibold uppercase tracking-wide',
                      isDark ? '!text-violet-200/85' : '!text-violet-600'
                    )}
                  >
                    Loja
                  </p>
                  <div className="mt-0.5">
                    <FairtradeStoreValue
                      storePrice={row.storePrice}
                      className="text-sm font-bold"
                      isDark={isDark}
                      accent
                    />
                  </div>
                </div>
              </div>

              <div
                className={cn(
                  'mt-3 flex items-center justify-between gap-2 border-t pt-3',
                  isDark ? 'border-blue-900/50' : 'border-gray-100'
                )}
              >
                <p
                  className={cn(
                    'font-poppins text-[10px] font-semibold uppercase tracking-wide',
                    isDark ? '!text-sky-200/80' : '!text-gray-500'
                  )}
                >
                  Ações
                </p>
                <div className="flex items-center gap-1.5">
                  <FairtradeCollectionActions row={row} isDark={isDark} />
                  <PowerFairtradeHistoryDialog
                    power={historySubject(row)}
                    triggerClassName={
                      isDark
                        ? undefined
                        : 'border-sky-200 bg-sky-50 text-sky-700 hover:border-sky-300 hover:bg-sky-100 hover:text-sky-800'
                    }
                  />
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
